// src/components/HelpPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
}

// ✅ Standard translate (message_key only)
const translateBackend = (t, data) => {
  if (!data) return t("msg_server_or_network");

  if (data.message_key) return t(data.message_key, data.message_params || {});

  if (Array.isArray(data.errors) && data.errors.length) {
    const first = data.errors[0];
    if (first?.key) return t(first.key, first.params || {});
    if (typeof first === "string") return t(first);
  }

  return t("msg_unknown_error");
};

const HelpPage = () => {
  const { t } = useTranslation();

  const width = useWindowWidth();
  const isMobile = width < 480;

  const API = process.env.REACT_APP_API_URL;

  const [application, setApplication] = useState("");

  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);

  // 🔴 vrai message erreur (rouge) seulement pour les vraies erreurs
  const [error, setError] = useState("");

  // 🔵 message info (bleu) pour cas "not found"
  const [info, setInfo] = useState("");

  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");

  // add message typed
  const [addMsg, setAddMsg] = useState({ type: "", text: "" }); // type: "error" | "success" | "info" | ""
  const [addLoading, setAddLoading] = useState(false);

  // ---------- INIT ----------
  useEffect(() => {
    const storedApp = localStorage.getItem("userApplication");
    if (storedApp) setApplication(storedApp);
  }, []);

  const fetchTasks = useCallback(async () => {
    if (!application || application.trim() === "") return;

    setLoadingTasks(true);
    setError("");
    setInfo("");

    try {
      const res = await axios.get(`${API}/api/help_tasks`, {
        params: { application },
      });
      setTasks(res.data?.tasks || []);
      setInfo(""); // si succès, pas d'info
    } catch (err) {
      const data = err?.response?.data;

      // ✅ cas spécial : help_tasks_not_found → BLEU (info)
      if (data?.message_key === "help_tasks_not_found") {
        setTasks([]);
        setError("");
        setInfo(t("help_tasks_not_found"));
      } else {
        setInfo("");
        setError(translateBackend(t, data));
      }
    } finally {
      setLoadingTasks(false);
    }
  }, [API, application, t]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const updateTask = async (id, newTitleHelp, newHelp) => {
    await axios.put(`${API}/api/help_tasks/${id}`, {
      title_help: newTitleHelp,
      help: newHelp,
    });

    setTasks((prev) =>
      prev.map((tt) =>
        tt.id === id ? { ...tt, title_help: newTitleHelp, help: newHelp } : tt
      )
    );
  };

  const deleteTask = async (id, onSuccess, onError) => {
    try {
      await axios.delete(`${API}/api/help_tasks/${id}`);
      setTasks((prev) => prev.filter((tt) => tt.id !== id));
      onSuccess();
    } catch (err) {
      onError(err);
    }
  };

  const addTask = async () => {
    setAddMsg({ type: "", text: "" });

    if (!newTitle.trim() || !newContent.trim()) {
      setAddMsg({ type: "error", text: t("help_add_required") });
      return;
    }
    if (!application.trim()) {
      setAddMsg({ type: "error", text: t("help_app_missing") });
      return;
    }

    setAddLoading(true);
    try {
      // compat ancien backend: application_name
      const res = await axios.post(`${API}/api/help_tasks`, {
        title_help: newTitle.trim(),
        help: newContent.trim(),
        application,
        application_name: application,
      });

      const task = res.data?.task || res.data;
      setTasks((prev) => [...prev, task]);

      setNewTitle("");
      setNewContent("");
      setAddMsg({ type: "success", text: t("help_add_success") });
      setAdding(false);

      // si on avait un message info "not found", on l'enlève car maintenant on a des tâches
      setInfo("");
    } catch (err) {
      setAddMsg({ type: "error", text: translateBackend(t, err?.response?.data) });
    } finally {
      setAddLoading(false);
    }
  };

  const addMsgClass =
    addMsg.type === "error"
      ? "mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      : addMsg.type === "success"
      ? "mb-4 rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success"
      : "mb-4 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-primary";

  const inputClass =
    "w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20";

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">{t("help_title")}</h1>
        {!adding && (
          <button
            onClick={() => {
              setAdding(true);
              setAddMsg({ type: "", text: "" });
            }}
            className={`rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 ${isMobile ? "w-full max-w-[220px]" : ""}`}
          >
            {t("help_add_new")}
          </button>
        )}
      </div>

      {loadingTasks ? (
        <p className="mt-6 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-primary">{t("loading")}</p>
      ) : error ? (
        <p className="mt-6 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</p>
      ) : info ? (
        <p className="mt-6 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-primary">{info}</p>
      ) : tasks.length === 0 ? (
        <p className="mt-6 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-primary">{t("help_tasks_not_found")}</p>
      ) : (
        <div className="mt-6 space-y-3">
          {tasks.map((task, index) => (
            <HelpTaskItem
              key={task.id ?? `task-${index}`}
              task={task}
              apiBase={API}
              onSave={updateTask}
              onDelete={deleteTask}
              onVideoUploaded={(id, video_path) => {
                setTasks((prev) =>
                  prev.map((tt) => (tt.id === id ? { ...tt, video_path } : tt))
                );
              }}
            />
          ))}
        </div>
      )}

      {adding && (
        <div className="mt-6 rounded-xl border border-border bg-card p-6">
          {addMsg.text && <p className={addMsgClass}>{addMsg.text}</p>}
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">{t("help_new_title_ph")}</label>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className={inputClass}
                disabled={addLoading}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-foreground">{t("help_new_content_ph")}</label>
              <textarea
                rows={4}
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                className={inputClass}
                disabled={addLoading}
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button
              onClick={() => {
                setAdding(false);
                setAddMsg({ type: "", text: "" });
                setNewTitle("");
                setNewContent("");
              }}
              className="rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground"
              disabled={addLoading}
            >
              {t("cancel")}
            </button>
            <button onClick={addTask} className="rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground" disabled={addLoading}>
              {addLoading ? t("saving") : t("help_add_btn")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const HelpTaskItem = ({ task, apiBase, onSave, onDelete, onVideoUploaded }) => {
  const { t } = useTranslation();

  const [isEditing, setIsEditing] = useState(false);

  const [title, setTitle] = useState(task.title_help || "");
  const [content, setContent] = useState(task.help || "");

  const [message, setMessage] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);

  const [showContent, setShowContent] = useState(false);

  // upload modal
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoFile, setVideoFile] = useState(null);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoMsg, setVideoMsg] = useState({ type: "", text: "" });
  const inputId = `video-input-${task.id}`;

  useEffect(() => setTitle(task.title_help || ""), [task.title_help]);
  useEffect(() => setContent(task.help || ""), [task.help]);

  const fullVideoUrl = useMemo(() => {
    if (!task.video_path) return "";
    if (/^https?:\/\//i.test(task.video_path)) return task.video_path;
    return `${apiBase}${task.video_path}`;
  }, [task.video_path, apiBase]);

  const hasVideo = !!fullVideoUrl;

  const handleSave = async () => {
    setMessage({ type: "", text: "" });
    if (!title.trim() || !content.trim()) {
      setMessage({ type: "error", text: t("help_item_empty") });
      return;
    }

    setLoading(true);
    try {
      await onSave(task.id, title.trim(), content.trim());
      setIsEditing(false);
      setMessage({ type: "success", text: t("help_item_saved") });
    } catch (err) {
      setMessage({ type: "error", text: translateBackend(t, err?.response?.data) });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t("help_confirm_delete_task"))) return;

    await onDelete(
      task.id,
      () => setMessage({ type: "success", text: t("help_deleted") }),
      (err) => setMessage({ type: "error", text: translateBackend(t, err?.response?.data) })
    );
  };

  const toggleContent = () => {
    if (!isEditing) setShowContent((prev) => !prev);
  };

  const openVideoDialog = () => {
    setVideoMsg({ type: "", text: "" });
    setVideoFile(null);
    setVideoModalOpen(true);
  };

  const closeVideoDialog = () => {
    if (videoUploading) return;
    setVideoModalOpen(false);
    setVideoMsg({ type: "", text: "" });
    setVideoFile(null);
  };

  const uploadVideo = async () => {
    setVideoMsg({ type: "", text: "" });

    if (!videoFile) {
      setVideoMsg({ type: "error", text: t("help_choose_video") });
      return;
    }

    setVideoUploading(true);
    try {
      const fd = new FormData();
      fd.append("video", videoFile);

      const res = await axios.post(`${apiBase}/api/help_tasks/${task.id}/video`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const video_path = res.data?.video_url || res.data?.video_path;
      if (video_path) onVideoUploaded(task.id, video_path);

      setVideoMsg({ type: "success", text: t("help_video_uploaded") });
      setShowContent(true);

      setTimeout(() => {
        setVideoModalOpen(false);
        setVideoFile(null);
      }, 250);
    } catch (err) {
      setVideoMsg({ type: "error", text: translateBackend(t, err?.response?.data) });
    } finally {
      setVideoUploading(false);
    }
  };

  const msgClass =
    message.type === "error"
      ? "mt-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      : "mt-3 rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success";

  const videoMsgClass =
    videoMsg.type === "error"
      ? "mt-3 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
      : "mt-3 rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success";

  const inputClass =
    "w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-ring/20";

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      {isEditing ? (
        <div className="space-y-3 px-6 py-4">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={inputClass}
            disabled={loading}
          />
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
            className={inputClass}
            disabled={loading}
          />

          <div className="flex justify-end gap-2">
            <button onClick={handleSave} className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground" disabled={loading}>
              {loading ? t("saving") : t("save")}
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground"
              disabled={loading}
            >
              {t("cancel")}
            </button>
          </div>
        </div>
      ) : (
        <>
          <button onClick={toggleContent} title={t("help_toggle_title")} className="flex w-full items-center justify-between px-6 py-4 text-start">
            <span className="text-sm font-semibold text-foreground">{title}</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`shrink-0 text-muted-foreground transition-transform ${showContent ? "rotate-180" : ""}`}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {showContent && (
            <div className="border-t border-border px-6 py-4">
              <p className="text-sm leading-relaxed text-muted-foreground">{content}</p>

              {hasVideo && (
                <div className="mt-4">
                  <video controls className="w-full max-h-80 rounded-lg border border-border">
                    <source src={fullVideoUrl} />
                  </video>
                </div>
              )}

              <div className="mt-4 flex gap-3">
                <button onClick={() => setIsEditing(true)} className="text-xs text-primary hover:underline">
                  {t("edit")}
                </button>
                <button onClick={handleDelete} className="text-xs text-destructive hover:underline">
                  {t("delete")}
                </button>
                <button className="text-xs text-accent hover:underline" onClick={openVideoDialog}>
                  {hasVideo ? t("help_change_video") : t("help_upload_video")}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {message.text && <p className={msgClass}>{message.text}</p>}

      {videoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 p-4" onClick={closeVideoDialog}>
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-foreground">
                {hasVideo ? t("help_change_video") : t("help_upload_video")}
              </h3>

              <button
                className="rounded-lg border border-border px-3 py-1.5 text-sm text-muted-foreground"
                onClick={closeVideoDialog}
                disabled={videoUploading}
              >
                {t("close")}
              </button>
            </div>

            <p className="mt-2 text-sm text-muted-foreground">
              {hasVideo ? t("help_video_replace_hint") : t("help_video_choose_hint")}
            </p>

            <input
              id={inputId}
              type="file"
              accept="video/*"
              onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
              disabled={videoUploading}
              className="sr-only"
            />

            <div className={`mt-4 flex items-center gap-2 rounded-lg border border-border p-2 ${videoUploading ? "opacity-60" : ""}`}>
              <button
                type="button"
                className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
                onClick={() => document.getElementById(inputId)?.click()}
                disabled={videoUploading}
              >
                {t("choose_file")}
              </button>

              <span className="truncate text-sm text-muted-foreground">
                {videoFile ? videoFile.name : t("no_file_selected")}
              </span>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60"
                onClick={uploadVideo}
                disabled={!videoFile || videoUploading}
              >
                {videoUploading ? t("uploading") : hasVideo ? t("replace") : t("upload")}
              </button>
            </div>

            {videoMsg.text && <p className={videoMsgClass}>{videoMsg.text}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpPage;
